import React, { useState } from 'react';
import SleekExplainField from './SleekExplainField';

const FinancialJudgment: React.FC = () => {
  const [judgmentAmount, setJudgmentAmount] = useState<string>('');
  // const [amountType, setAmountType] = useState<string>(''); // Reserved for future use
  const [paidInFull, setPaidInFull] = useState<string>('');
  const [actionsTaken, setActionsTaken] = useState<string>('');
  const [courtName, setCourtName] = useState<string>('');
  const [showExplanation, setShowExplanation] = useState<boolean>(false);

  const handlePaidInFullChange = (value: string) => {
    setPaidInFull(value);
    if (value === 'unknown') {
      setShowExplanation(true);
    } else {
      setShowExplanation(false);
    }
  };

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Civil Judgment</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Civil Judgment:</strong> Provide your civil judgment details
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Judgment Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Civil Judgment Amount
              </label>
              <input
                type="text"
                value={judgmentAmount}
                onChange={(e) => setJudgmentAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gov-blue focus:border-gov-blue"
                placeholder="Enter amount..."
              />
            </div>

            {/* When was the judgment entered */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                When was the judgment entered against you?
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gov-blue focus:border-gov-blue">
                    <option value="">Select Month</option>
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                    <option value="estimated">Estimated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gov-blue focus:border-gov-blue"
                    placeholder="YYYY"
                    min="1900"
                    max="2024"
                  />
                </div>
              </div>
            </div>

            {/* Paid in Full Question */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Did you pay this judgment in full?
              </h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paidInFull"
                    value="yes"
                    checked={paidInFull === 'yes'}
                    onChange={(e) => handlePaidInFullChange(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paidInFull"
                    value="no"
                    checked={paidInFull === 'no'}
                    onChange={(e) => handlePaidInFullChange(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">No</span>
                </label>
              </div>

              {paidInFull === 'yes' && (
                <div className="mt-4">
                  <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium">
                    Open Answer
                  </button>
                </div>
              )}
            </div>

            {/* Actions Taken */}
            <div>
              <SleekExplainField
                label="What actions have you taken to pay this judgment in full?"
                placeholder="Describe actions taken..."
                helpText="Use the AI Assistant to help provide a comprehensive explanation of your actions to pay the judgment."
                value={actionsTaken}
                onChange={setActionsTaken}
                fieldType="financial-judgment-actions"
              />
              <div className="mt-2">
                <button className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">
                  More
                </button>
              </div>
            </div>

            {/* Court Name */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                What is the name of the court involved? (Do not use acronyms or 
                abbreviations.)
              </h3>
              <input
                type="text"
                value={courtName}
                onChange={(e) => setCourtName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gov-blue focus:border-gov-blue"
                placeholder="Enter court name..."
              />
            </div>

            {/* Explanation Field with AI Assist */}
            {showExplanation && (
              <SleekExplainField
                label="Please explain:"
                placeholder="Please provide additional details about the judgment payment status..."
                helpText="Use the AI Assistant to help provide a comprehensive explanation about your judgment payment status."
                fieldType="financial-judgment-actions"
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
            <button className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
              Previous
            </button>
            <div className="space-x-3">
              <button className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                Save Progress
              </button>
              <button className="px-6 py-2 bg-gov-blue text-white rounded-md hover:bg-blue-800">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialJudgment;